// src/features/coupon/components/CouponCard.tsx

import React from 'react';

import {
  CheckCircle2,
  Clock3,
  Gift,
  Sparkles,
  Store,
  Ticket,
  XCircle,
} from 'lucide-react';

import type {
  CouponRewardType,
  UserCoupon,
} from '../types/coupon.types';

import {
  canUserUseCoupon,
} from '../utils/coupon.utils';


interface CouponCardProps {
  coupon: UserCoupon;

  storeName?: string;

  using?: boolean;

  onUse: (
    coupon: UserCoupon,
  ) => void;
}


const formatNumber = (
  value: number | null,
): string => {

  if (value == null) {
    return '-';
  }

  return value.toLocaleString(
    'ko-KR',
  );
};


const formatDate = (
  value: string | null,
): string => {

  if (!value) {
    return '-';
  }

  const date =
    new Date(value);

  if (
    Number.isNaN(
      date.getTime(),
    )
  ) {
    return value;
  }

  return new Intl.DateTimeFormat(
    'ko-KR',
    {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    },
  ).format(date);
};


const getRewardLabel = (
  rewardType:
    CouponRewardType | null,
  rewardAmount:
    number | null,
): string => {

  if (!rewardType) {
    return '-';
  }

  switch (rewardType) {

    case 'POINT':
      return `${formatNumber(
        rewardAmount,
      )}LS`;

    case 'STORE_POINT':
      return `${formatNumber(
        rewardAmount,
      )} 매장P`;

    case 'FREE_GAME':
      return `무료게임 ${formatNumber(
        rewardAmount,
      )}회`;

    case 'SATELLITE':
      return `새틀라이트 ${formatNumber(
        rewardAmount,
      )}회`;

    case 'SEAT':
      return `시트권 ${formatNumber(
        rewardAmount,
      )}매`;

    case 'ETC':
      return `기타 보상 ${formatNumber(
        rewardAmount,
      )}`;

    default:
      return '-';
  }
};


const getStatusLabel = (
  status: UserCoupon['status'],
): string => {

  switch (status) {

    case 'AVAILABLE':
      return '사용 가능';

    case 'USED':
      return '사용 완료';

    case 'EXPIRED':
      return '기간 만료';

    case 'CANCELLED':
      return '사용 취소';

    default:
      return status;
  }
};


const getStatusClassName = (
  status: UserCoupon['status'],
): string => {

  switch (status) {

    case 'AVAILABLE':
      return `
        border-emerald-200
        bg-emerald-50
        text-emerald-700
      `;

    case 'USED':
      return `
        border-gray-200
        bg-gray-100
        text-gray-600
      `;

    case 'EXPIRED':
      return `
        border-orange-200
        bg-orange-50
        text-orange-700
      `;

    case 'CANCELLED':
      return `
        border-red-200
        bg-red-50
        text-red-600
      `;

    default:
      return `
        border-gray-200
        bg-gray-50
        text-gray-600
      `;
  }
};


const CouponCard:
  React.FC<CouponCardProps> = ({
    coupon,
    storeName,
    using = false,
    onUse,
  }) => {

    const userCanUse =
      canUserUseCoupon(
        coupon,
      );


    const isRandom =
      coupon.rewardMode ===
      'RANDOM';


    const isAvailable =
      coupon.status ===
      'AVAILABLE';


    /**
     * RANDOM 사용 완료 쿠폰이면
     * 실제 당첨 보상 표시
     */
    const displayedRewardType =
      coupon.status === 'USED' &&
      isRandom
        ? coupon.selectedRewardType
        : coupon.rewardType;


    const displayedRewardAmount =
      coupon.status === 'USED' &&
      isRandom
        ? coupon.selectedRewardAmount
        : coupon.rewardAmount;


    const displayedReward =
      isRandom &&
      isAvailable
        ? '랜덤 포인트 보상'
        : getRewardLabel(
            displayedRewardType,
            displayedRewardAmount,
          );


    return (
      <article
        className="
          overflow-hidden
          rounded-xl
          border
          border-gray-200
          bg-white
          shadow-sm
          transition
          hover:border-gray-300
          hover:shadow
        "
      >

        {/*
         * ==============================
         * 상단
         * ==============================
         */}
        <div
          className="
            px-4
            pb-3
            pt-3.5
          "
        >

          <div
            className="
              flex
              items-start
              justify-between
              gap-3
            "
          >

            <div className="min-w-0 flex-1">

              {/*
               * 쿠폰명
               */}
              <div
                className="
                  flex
                  items-center
                  gap-1.5
                "
              >

                {isRandom ? (
                  <Sparkles
                    size={16}
                    className="
                      shrink-0
                      text-main
                    "
                  />
                ) : (
                  <Ticket
                    size={16}
                    className="
                      shrink-0
                      text-main
                    "
                  />
                )}


                <h3
                  className="
                    truncate
                    text-[15px]
                    font-bold
                    text-gray-900
                  "
                >
                  {coupon.couponName}
                </h3>

              </div>


              {/*
               * 설명
               */}
              {coupon.description && (
                <p
                  className="
                    mt-1
                    truncate
                    text-xs
                    text-gray-500
                  "
                >
                  {coupon.description}
                </p>
              )}

            </div>


            {/*
             * 상태
             */}
            <span
              className={`
                shrink-0
                rounded-full
                border
                px-2
                py-0.5
                text-[10px]
                font-bold
                ${getStatusClassName(
                  coupon.status,
                )}
              `}
            >
              {getStatusLabel(
                coupon.status,
              )}
            </span>

          </div>


          {/*
           * ==============================
           * 보상
           * ==============================
           */}
          <div
            className="
              mt-3
              flex
              items-center
              gap-2
            "
          >

            <div
              className="
                flex
                h-8
                w-8
                shrink-0
                items-center
                justify-center
                rounded-lg
                bg-gray-50
                text-main
              "
            >
              <Gift
                size={16}
              />
            </div>


            <div className="min-w-0">

              <p
                className="
                  text-[10px]
                  font-medium
                  text-gray-400
                "
              >
                보상
              </p>


              <p
                className="
                  truncate
                  text-sm
                  font-bold
                  text-gray-900
                "
              >
                {displayedReward}
              </p>

            </div>


            {isRandom &&
              isAvailable && (
                <span
                  className="
                    ml-auto
                    shrink-0
                    text-[10px]
                    text-gray-400
                  "
                >
                  사용 시 결정
                </span>
              )}

          </div>


          {/*
           * ==============================
           * 유효기간 / 매장
           * ==============================
           */}
          <div
            className="
              mt-3
              flex
              flex-wrap
              items-center
              gap-x-4
              gap-y-1
              border-t
              border-gray-100
              pt-2.5
              text-[11px]
              text-gray-500
            "
          >

            <div
              className="
                flex
                items-center
                gap-1
              "
            >
              <Clock3
                size={13}
                className="
                  shrink-0
                  text-gray-400
                "
              />

              <span>
                {formatDate(
                  coupon.expiresAt,
                )}까지
              </span>
            </div>


            {coupon.storeId != null && (
              <div
                className="
                  flex
                  min-w-0
                  items-center
                  gap-1
                "
              >
                <Store
                  size={13}
                  className="
                    shrink-0
                    text-gray-400
                  "
                />

                <span className="truncate">
                  {storeName ??
                    `매장 #${coupon.storeId}`}
                </span>
              </div>
            )}

          </div>

        </div>


        {/*
         * ==============================
         * USED
         * ==============================
         */}
        {coupon.status ===
          'USED' && (
          <div
            className="
              flex
              items-center
              gap-1.5
              border-t
              border-gray-100
              bg-gray-50
              px-4
              py-2
              text-[11px]
              text-gray-500
            "
          >
            <CheckCircle2
              size={13}
              className="
                shrink-0
                text-emerald-500
              "
            />

            {formatDate(
              coupon.usedAt,
            )}{' '}
            사용 완료
          </div>
        )}


        {/*
         * ==============================
         * EXPIRED
         * ==============================
         */}
        {coupon.status ===
          'EXPIRED' && (
          <div
            className="
              flex
              items-center
              gap-1.5
              border-t
              border-orange-100
              bg-orange-50
              px-4
              py-2
              text-[11px]
              text-orange-700
            "
          >
            <Clock3
              size={13}
            />

            사용기간이 만료되었습니다.
          </div>
        )}


        {/*
         * ==============================
         * CANCELLED
         * ==============================
         */}
        {coupon.status ===
          'CANCELLED' && (
          <div
            className="
              flex
              items-center
              gap-1.5
              border-t
              border-red-100
              bg-red-50
              px-4
              py-2
              text-[11px]
              text-red-600
            "
          >
            <XCircle
              size={13}
            />

            취소된 쿠폰입니다.
          </div>
        )}


        {/*
         * ==============================
         * 사용 버튼
         * ==============================
         */}
        {isAvailable && (
          <div
            className="
              border-t
              border-gray-100
              px-3
              py-2.5
            "
          >

            {userCanUse ? (
              <button
                type="button"
                disabled={using}
                onClick={() =>
                  onUse(coupon)
                }
                className="
                  flex
                  h-9
                  w-full
                  items-center
                  justify-center
                  rounded-lg
                  bg-main
                  px-3
                  text-xs
                  font-bold
                  text-white
                  transition
                  hover:opacity-90
                  disabled:cursor-not-allowed
                  disabled:opacity-50
                "
              >
                {using
                  ? '처리 중...'
                  : isRandom
                    ? '랜덤 보상 받기'
                    : '사용하기'}
              </button>
            ) : (
              <div
                className="
                  flex
                  h-9
                  items-center
                  justify-center
                  rounded-lg
                  bg-gray-50
                  px-3
                  text-xs
                  font-semibold
                  text-gray-500
                "
              >
                직원에게 쿠폰을 보여주세요
              </div>
            )}

          </div>
        )}

      </article>
    );
  };


export default CouponCard;